import { request } from "@src/utils/apiClient";

export const getCustomers = async () => {
  return request({
    method: "GET",
    endpoint: "/adminDashboard/customers",
  });
};

// Returns a presigned S3 PUT url for one of the customer's email logos
// (logoType: "square" | "standard" | "footer").
export const getCustomerLogoUploadUrl = async (customerCode, logoType) => {
  return request({
    method: "POST",
    endpoint: `/adminDashboard/customers/${customerCode}/logos`,
    body: { logoType },
  });
};

// Direct S3 PUT — deliberately NOT via apiClient (no auth header, raw binary body).
export const uploadFileToPresignedUrl = async (uploadUrl, file) => {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "image/png" },
    body: file,
  });
  if (!res.ok) throw new Error(`S3 upload failed (${res.status})`);
  return res;
};

export const getBehaviours = async (customerCode) => {
  const req = {
    method: "GET",
    endpoint: "/adminDashboard/behaviours",
  };
  if (customerCode) {
    req.params = { customerCode };
  }
  return request(req);
};

export const getOperators = async (customerCode) => {
  const req = {
    method: "GET",
    endpoint: "/adminDashboard/operators",
  };
  if (customerCode) {
    req.params = { customerCode };
  }
  return request(req);
};

export const getOperator = async (operatorId) => {
  const req = {
    method: "GET",
    endpoint: `/adminDashboard/operators/${operatorId}`,
  };
  return request(req);
};
export const getOperatorNotifications = async (customerCode, operatorCode) => {
  const req = {
    method: "GET",
    endpoint: `/adminDashboard/notifications/customers/${customerCode}/operators/${operatorCode}`,
  };
  return request(req);
};
export const getNotification = async (notificationId) => {
  const req = {
    method: "GET",
    endpoint: `/adminDashboard/notifications/${notificationId}`,
  };
  return request(req);
};
// Per-operator most recent sentAt for `template`, across every operator of `customerCode`
// that has ever received it. Operators absent from the response have never received it.
export const getLastSentDates = async (customerCode, template) => {
  const req = {
    method: "GET",
    endpoint: `/adminDashboard/notifications/customers/${customerCode}/last-sent`,
    params: { template },
  };
  return request(req);
};
export const getNotificationTemplateConfigs = async () => {
  const req = {
    method: "GET",
    endpoint: `/adminDashboard/notifications/template-configs`,
  };
  return request(req);
};
export const getOperatorSignupLink = async (customerCode, operatorCode) => {
  const req = {
    method: "GET",
    endpoint: `/adminDashboard/notifications/customers/${customerCode}/operators/${operatorCode}/signup-link`,
  };
  return request(req);
};

export const getDynamicContentSettings = async () => {
  return request({
    endpoint: "/dynamic_content/settings",
    method: "GET",
  });
};

export const saveDynamicContent = (replacement) => {
  return request({
    endpoint: "/dynamic_content/values",
    method: "POST",
    body: JSON.stringify(replacement),
  });
};

export const deleteDynamicContent = (replacement) => {
  return request({
    endpoint: "/dynamic_content/values",
    method: "DELETE",
    body: JSON.stringify(replacement),
  });
};

export const deployDynamicContent = (mode) => {
  return request({
    endpoint: "/adminDashboard/_/dynamic-content-deployer",
    method: "POST",
    body: { mode },
  });
};

export const clearBackendCaches = () => {
  request({
    endpoint: "/operatorDashboard/_/cache",
    method: "DELETE",
  });
  return request({
    endpoint: "/managerDashboard/_/clear-caches",
    method: "DELETE",
  });
};

export const createCustomerIdentityPool = (customer) => {
  return request({
    endpoint: "/identity/customers",
    method: "POST",
    body: {
      name: customer.name,
      code: customer.customerCode,
      requiredEmail: customer.industry === "aviation",
      sso: false,
    },
  });
};

export const createCustomer = (customer) => {
  return request({
    endpoint: "/adminDashboard/customers",
    method: "POST",
    body: customer,
  });
};

export const updateCustomer = (customer) => {
  return request({
    endpoint: "/adminDashboard/customers",
    method: "PUT",
    body: customer,
  });
};

export function updateBehaviour(behaviour) {
  const req = {
    endpoint: "/adminDashboard/behaviours",
    method: "PUT",
    body: behaviour,
  };
  return request(req);
}

export function createBehaviour(behaviour) {
  const req = {
    endpoint: "/adminDashboard/behaviours",
    method: "POST",
    body: behaviour,
  };
  return request(req);
}

export function deleteBehaviour(id) {
  const req = {
    endpoint: "/adminDashboard/behaviours",
    method: "DELETE",
    params: { id },
  };
  return request(req);
}

export function updateCharity(charity) {
  const req = {
    endpoint: "/adminDashboard/charities",
    method: "PUT",
    body: charity,
  };
  return request(req);
}

export function createCharity(charity) {
  const req = {
    endpoint: "/adminDashboard/charities",
    method: "POST",
    body: charity,
  };
  return request(req);
}

export function getCharities(customerCode) {
  const req = {
    endpoint: "/adminDashboard/charities",
    method: "GET",
    params: { customerCode },
  };
  return request(req);
}

export function deleteCharity(id) {
  const req = {
    endpoint: "/adminDashboard/charities",
    method: "DELETE",
    params: { id },
  };
  return request(req);
}

export function getTeams(customerId) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams`,
    method: "GET",
  };
  return request(req);
}

export function deleteTeam(customerId, teamId) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams/${teamId}`,
    method: "DELETE",
  };
  return request(req);
}

export function updateTeam(customerId, team) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams`,
    method: "PUT",
    body: team,
  };
  return request(req);
}

export function createTeam(customerId, team) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams`,
    method: "POST",
    body: team,
  };
  return request(req);
}

export function updateTarget(customerId, teamId, target) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams/${teamId}/targets/${target.id}`,
    method: "PUT",
    body: target,
  };
  return request(req);
}

export function createTarget(customerId, teamId, target) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams/${teamId}/targets`,
    method: "POST",
    body: [target],
  };
  return request(req);
}

export function deleteTarget(customerId, teamId, targetId) {
  const req = {
    endpoint: `/adminDashboard/customers/${customerId}/teams/${teamId}/targets/${targetId}`,
    method: "DELETE",
  };
  return request(req);
}

export const getDonationsTool = async (customerCode) => {
  return request({
    method: "GET",
    endpoint: `/adminDashboard/donations-tool/${customerCode.toLowerCase()}`,
  });
};

/** @param body SnsTriggerReportDto from Reports Service */
export function triggerReportsService(snsTriggerReportDto) {
  const req = {
    endpoint: `/adminDashboard/_/send-report`,
    method: "POST",
    body: { reportSns: snsTriggerReportDto },
  };
  return request(req);
}

// Behaviour Types (global)
export function getBehaviourTypes() {
  return request({
    endpoint: "/adminDashboard/behaviour-types",
    method: "GET",
  });
}

export function createBehaviourType(data) {
  return request({
    endpoint: "/adminDashboard/behaviour-types",
    method: "POST",
    body: data,
  });
}

export function updateBehaviourType(data) {
  return request({
    endpoint: "/adminDashboard/behaviour-types",
    method: "PUT",
    body: data,
  });
}

export function deleteBehaviourType(id) {
  return request({
    endpoint: "/adminDashboard/behaviour-types",
    method: "DELETE",
    params: { id },
  });
}

// Behaviour Groups (global)
export function getBehaviourGroups() {
  return request({
    endpoint: "/adminDashboard/behaviour-groups",
    method: "GET",
  });
}

export function createBehaviourGroup(data) {
  return request({
    endpoint: "/adminDashboard/behaviour-groups",
    method: "POST",
    body: data,
  });
}

export function updateBehaviourGroup(data) {
  return request({
    endpoint: "/adminDashboard/behaviour-groups",
    method: "PUT",
    body: data,
  });
}

export function deleteBehaviourGroup(id) {
  return request({
    endpoint: "/adminDashboard/behaviour-groups",
    method: "DELETE",
    params: { id },
  });
}

// Customer Behaviour Groups (per-customer group description overrides)
export function getCustomerBehaviourGroups(customerId) {
  return request({
    endpoint: "/adminDashboard/customer-behaviour-groups",
    method: "GET",
    params: { customerId },
  });
}

export function createCustomerBehaviourGroup(data) {
  return request({
    endpoint: "/adminDashboard/customer-behaviour-groups",
    method: "POST",
    body: data,
  });
}

export function updateCustomerBehaviourGroup(data) {
  return request({
    endpoint: "/adminDashboard/customer-behaviour-groups",
    method: "PUT",
    body: data,
  });
}

export function deleteCustomerBehaviourGroup(id) {
  return request({
    endpoint: "/adminDashboard/customer-behaviour-groups",
    method: "DELETE",
    params: { id },
  });
}
